import pdfMake from 'pdfmake/build/pdfmake'
import vfsFonts from 'pdfmake/build/vfs_fonts'
import dayjs from 'dayjs'
import api from '../utils/api'
import { formatCurrency, numberToThaiText } from '../utils/helpers'
import { firstNonBlank } from '../utils/contactDisplayName'

pdfMake.vfs = vfsFonts
pdfMake.fonts = {
  THSarabunNew: {
    normal: 'THSarabunNew.ttf',
    bold: 'THSarabunNew Bold.ttf',
    italics: 'THSarabunNew Italic.ttf',
    bolditalics: 'THSarabunNew BoldItalic.ttf'
  }
}

const blobToDataUrl = blob => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result)
  reader.onerror = reject
  reader.readAsDataURL(blob)
})

const loadProtectedImage = async attachment => {
  if (!attachment?.url || !String(attachment.mime_type || '').startsWith('image/')) return null
  try {
    const response = await api.get(attachment.url, { responseType: 'blob' })
    return await blobToDataUrl(response.data)
  } catch (error) {
    console.error('Cannot load expense certificate attachment:', attachment.url, error)
    return null
  }
}

const borderedCell = (text, options = {}) => ({
  text: text ?? '',
  bold: Boolean(options.bold),
  alignment: options.alignment || 'left',
  margin: options.margin || [4, 6, 4, 6],
  fontSize: options.fontSize || 14
})

const attachmentBox = (title, image, fit) => ({
  stack: [
    { text: title, bold: true, fontSize: 15, color: '#344054', margin: [0, 0, 0, 6] },
    image
      ? { image, fit, alignment: 'center', margin: [0, 0, 0, 4] }
      : {
          table: {
            widths: ['*'],
            heights: fit[1],
            body: [[{ text: 'ไม่มีรูปแนบ', alignment: 'center', color: '#98a2b3', margin: [0, Math.max(20, fit[1] / 2 - 12), 0, 0] }]]
          },
          layout: { hLineColor: () => '#d0d5dd', vLineColor: () => '#d0d5dd', hLineStyle: () => ({ dash: { length: 3 } }), vLineStyle: () => ({ dash: { length: 3 } }) }
        }
  ]
})

export const generateExpenseCertificatePDF = async (
  expense,
  company = {},
  supplierData = null,
  options = {}
) => {
  const attachments = Array.isArray(expense?.attachments) ? expense.attachments : []
  const slipAttachment = attachments.find(file => file.attachment_type === 'payment_slip')
  const productAttachments = attachments
    .filter(file => file.attachment_type === 'product_image')
    .slice(0, 3)

  const [slipImage, ...productImages] = await Promise.all([
    loadProtectedImage(slipAttachment),
    ...productAttachments.map(loadProtectedImage)
  ])

  const companyName = firstNonBlank(company.company_name, company.name, 'บริษัท')
  const supplierName = firstNonBlank(
    supplierData?.company_name,
    supplierData?.name,
    expense.supplier_company,
    expense.supplier_name,
    '-'
  )
  const recorderName = firstNonBlank(options.recorder_name, expense.created_by_name, company.signature_name, '-')
  const approverName = firstNonBlank(expense.approved_by_name, options.approver_name, '')
  const expenseDate = dayjs(expense.expense_date)
  const dateText = expenseDate.isValid() ? expenseDate.format('DD/MM/YYYY') : '-'
  const totalAmount = Number(expense.total_amount || (Number(expense.amount || 0) + Number(expense.tax_amount || 0)))
  const sourceItems = Array.isArray(expense.items) && expense.items.length
    ? expense.items.slice(0, 8)
    : [{ description: expense.description || '-', quantity: 1, unit_price: totalAmount, total_price: totalAmount }]

  const detailRows = sourceItems.map((item, index) => [
    borderedCell(index === 0 ? dateText : '', { alignment: 'center' }),
    borderedCell(item.description || expense.description || '-'),
    borderedCell(formatCurrency(Number(item.total_price ?? (Number(item.quantity || 1) * Number(item.unit_price || 0)))), { alignment: 'right' }),
    borderedCell(index === 0 ? (expense.notes || '') : '')
  ])
  while (detailRows.length < 8) {
    detailRows.push([
      borderedCell(' ', { margin: [4, 8, 4, 8] }),
      borderedCell(' ', { margin: [4, 8, 4, 8] }),
      borderedCell(' ', { margin: [4, 8, 4, 8] }),
      borderedCell(' ', { margin: [4, 8, 4, 8] })
    ])
  }

  const productColumns = [0, 1, 2].map(index => ({
    width: '*',
    ...attachmentBox(`รูปสินค้า/บริการ ${index + 1}`, productImages[index], [150, 165]),
    margin: index === 1 ? [8, 0, 8, 0] : [0, 0, 0, 0]
  }))

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [30, 28, 30, 30],
    background: () => ({
      canvas: [{ type: 'rect', x: 18, y: 18, w: 559, h: 806, r: 2, lineWidth: 0.7, lineColor: '#98a2b3' }]
    }),
    content: [
      { text: 'ใบรับรองแทนใบเสร็จรับเงิน', alignment: 'center', bold: true, fontSize: 22, margin: [0, 4, 0, 4] },
      {
        text: [
          { text: 'บจ. / หจก. ' },
          { text: supplierName, bold: true },
          { text: '  (ผู้ซื้อ/ผู้รับบริการ)' }
        ],
        alignment: 'center',
        fontSize: 16,
        margin: [0, 0, 0, 12]
      },
      {
        table: {
          headerRows: 1,
          widths: [105, '*', 82, 85],
          body: [
            [
              borderedCell('วัน เดือน ปี', { alignment: 'center', bold: true }),
              borderedCell('รายละเอียดรายจ่าย', { alignment: 'center', bold: true }),
              borderedCell('จำนวนเงิน', { alignment: 'center', bold: true }),
              borderedCell('หมายเหตุ', { alignment: 'center', bold: true })
            ],
            ...detailRows,
            [
              { text: 'รวมทั้งสิ้น', bold: true, alignment: 'center', colSpan: 2, margin: [4, 6, 4, 6] }, {},
              borderedCell(formatCurrency(totalAmount), { alignment: 'right', bold: true }),
              borderedCell('')
            ]
          ]
        },
        layout: {
          hLineColor: () => '#667085',
          vLineColor: () => '#667085',
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6
        }
      },
      {
        text: [
          { text: 'รวมทั้งสิ้น (ตัวอักษร)  ', bold: true },
          { text: numberToThaiText(totalAmount) }
        ],
        fontSize: 15,
        margin: [70, 16, 0, 12]
      },
      {
        text: [
          { text: 'ข้าพเจ้า ' },
          { text: recorderName, bold: true },
          { text: ' (ผู้เบิกจ่าย) ขอรับรองว่ารายจ่ายข้างต้นไม่อาจเรียกเก็บใบเสร็จรับเงินจากผู้รับได้ และข้าพเจ้าได้จ่ายไปในงานของ ' },
          { text: companyName, bold: true },
          { text: ` โดยแท้ ตั้งแต่วันที่ ${dateText}` }
        ],
        fontSize: 15,
        lineHeight: 1.25,
        margin: [8, 0, 8, 14]
      },
      {
        columns: [
          { width: '*', text: '' },
          {
            width: 265,
            stack: [
              { text: `ลงชื่อ .......................................................... (ผู้เบิกจ่าย)`, alignment: 'left', margin: [0, 4, 0, 8] },
              { text: `( ${recorderName} )`, alignment: 'center', margin: [0, 0, 0, 8] },
              { text: 'ลงชื่อ .......................................................... (ผู้อนุมัติ)', alignment: 'left', margin: [0, 4, 0, 8] },
              { text: approverName ? `( ${approverName} )` : '( .......................................................... )', alignment: 'center' }
            ],
            fontSize: 14
          }
        ]
      },
      { text: 'เอกสารแนบใบรับรองแทนใบเสร็จรับเงิน', pageBreak: 'before', alignment: 'center', bold: true, fontSize: 21, color: '#175cd3', margin: [0, 4, 0, 2] },
      { text: `${expense.expense_number || '-'}  |  วันที่ ${dateText}  |  ${supplierName}`, alignment: 'center', fontSize: 14, color: '#667085', margin: [0, 0, 0, 12] },
      attachmentBox('หลักฐานการชำระเงิน (1 สลิป)', slipImage, [490, 285]),
      { text: 'รูปสินค้า/บริการ', bold: true, fontSize: 16, color: '#344054', margin: [0, 14, 0, 8] },
      { columns: productColumns }
    ],
    footer: (currentPage, pageCount) => ({
      text: `หน้า ${currentPage}/${pageCount}`,
      alignment: 'right',
      margin: [0, 0, 30, 0],
      fontSize: 11,
      color: '#667085'
    }),
    defaultStyle: { font: 'THSarabunNew', fontSize: 14, color: '#101828' }
  }

  return new Promise((resolve, reject) => {
    try {
      pdfMake.createPdf(docDefinition).getDataUrl(resolve)
    } catch (error) {
      reject(error)
    }
  })
}

export default generateExpenseCertificatePDF
